/** @jsxImportSource preact */
import type { ProductCardData } from '../types';
import type { ActionHandler } from './types';
import { sanitizeUrl } from '../services/sanitize';
import { htmlToText } from '../services/htmlText';

interface ProductDetailProps {
  product: ProductCardData;
  onAction: ActionHandler;
}

function formatPrice(product: ProductCardData): string {
  if (typeof product.price !== 'number' || !Number.isFinite(product.price)) {
    return '';
  }
  const amount = product.price.toLocaleString('es-AR');
  return product.currency ? `${product.currency} ${amount}` : `$${amount}`;
}

function formatCompareAt(product: ProductCardData): string {
  const value = product.compare_at_price;
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  if (typeof product.price === 'number' && value <= product.price) return '';
  const amount = value.toLocaleString('es-AR');
  return product.currency ? `${product.currency} ${amount}` : `$${amount}`;
}

/**
 * Product detail view — the editorial "tap a card → see the piece" state.
 * Rendered as its own turn in the thread when a `view-product` intent is
 * dispatched from the carousel. Holds the add-to-cart CTA (the carousel
 * has none) and, when present, an out-of-shop link.
 *
 * The add CTA emits the existing `add-to-cart` intent so the cart /
 * checkout contract with chat_ms is unchanged. Never uses
 * dangerouslySetInnerHTML.
 */
export function ProductDetail({ product, onAction }: ProductDetailProps) {
  if (!product) return null;
  const imageUrl = sanitizeUrl(product.image_url || '');
  const productUrl = sanitizeUrl(product.url || '');
  const priceStr = formatPrice(product);
  const compareStr = formatCompareAt(product);
  // Catalog descriptions are HTML: show them as plain text (never innerHTML).
  const descText = htmlToText(product.description);

  return (
    <div class="lc-product-detail">
      <div class="lc-product-detail__media">
        {imageUrl ? (
          <img src={imageUrl} alt={product.name || ''} loading="lazy" />
        ) : (
          <div class="lc-product-detail__placeholder" data-empty="true" aria-hidden="true" />
        )}
      </div>
      <div class="lc-product-detail__body">
        <h3 class="lc-product-detail__name">{product.name}</h3>
        <div class="lc-product-detail__price-row">
          {priceStr ? <span class="lc-product-detail__price">{priceStr}</span> : null}
          {compareStr ? (
            <span class="lc-product-detail__compare">{compareStr}</span>
          ) : null}
        </div>
        {descText ? (
          <p class="lc-product-detail__desc">{descText}</p>
        ) : null}
        <button
          type="button"
          class="lc-add-cart-btn"
          data-product-id={product.product_id}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAction({ kind: 'add-to-cart', product });
          }}
        >
          Agregar a la bolsa
        </button>
        {productUrl ? (
          <a
            href={productUrl}
            target="_blank"
            rel="noopener noreferrer"
            class="lc-product-link"
          >
            Ver en la tienda
          </a>
        ) : null}
      </div>
    </div>
  );
}
